import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  TextField,
  FormControl,
  Select,
  MenuItem,
  OutlinedInput,
  Checkbox,
  ListItemText,
  Chip,
  CircularProgress,
  Autocomplete
} from '@mui/material';
import {
  Send as SendIcon,
  Schedule as ScheduleIcon,
  Pause as PauseIcon,
  Stop as StopIcon
} from '@mui/icons-material';
import { LutienApi } from '../../../services/LutienApi';
import { NotificationEngineSourceDTO } from '../../../services/Hub/notificationCenter/types';
import { SENDING_BEHAVIOR_OPTIONS } from './constants';

interface DispatchSetup {
  id: string;
  notificationMessageTemplateId: string;
  recipientUserIds: string[];
  additionalPayloadDefinitionConfig?: {
    sendingBehavior?: string;
  };
}

interface OrgUser {
  id: string;
  name: string;
  email: string;
}

interface DispatchSetupsEditorProps {
  dispatchSetups?: DispatchSetup;
  organizationId?: string;
  engineSourceFields?: NotificationEngineSourceDTO[];
  onDispatchSetupChange?: (setup: DispatchSetup) => void;
  dispatchSetupData?: DispatchSetup;
}

// Generate dynamic templates from engine source fields
const generateDynamicTemplates = (engineSourceFields: NotificationEngineSourceDTO[] = []) => {
  return engineSourceFields.map(field => ({
    value: `{{ context.${field.fieldIdentifier.replace(/_/g, '.')} }}`,
    label: field.description || field.fieldIdentifier
  }));
};

export default function DispatchSetupsEditor({
  dispatchSetups,
  organizationId,
  engineSourceFields,
  onDispatchSetupChange,
  dispatchSetupData
}: DispatchSetupsEditorProps) {
  const [isLoadingUsers, setIsLoadingUsers] = useState(false);
  const [orgUsers, setOrgUsers] = useState<OrgUser[]>([]);

  // Use external data if provided, otherwise use dispatchSetups as fallback
  const currentSetup = dispatchSetupData ||
    dispatchSetups || {
      id: '',
      notificationMessageTemplateId: '',
      recipientUserIds: [],
      additionalPayloadDefinitionConfig: undefined
    };

  useEffect(() => {
    if (organizationId) {
      loadOrganizationUsers(organizationId);
    } else {
      setOrgUsers([]);
    }
  }, [organizationId]);

  useEffect(() => {
    if (!currentSetup.additionalPayloadDefinitionConfig?.sendingBehavior) {
      const newConfig = {
        ...currentSetup.additionalPayloadDefinitionConfig,
        sendingBehavior: 'SEND_AUTOMATICALLY'
      };
      handleSetupChange('additionalPayloadDefinitionConfig', newConfig);
    }
  }, []);

  const loadOrganizationUsers = async (orgId: string) => {
    setIsLoadingUsers(true);
    try {
      const memberships = await LutienApi.getMemberships(orgId);
      const users = memberships.map(membership => ({
        id: membership.user.id,
        name: membership.user.name,
        email: membership.user.email
      }));
      setOrgUsers(users);
    } catch (error) {
      console.error('Error fetching organization users:', error);
      setOrgUsers([]);
    } finally {
      setIsLoadingUsers(false);
    }
  };

  const handleSetupChange = (field: keyof DispatchSetup, value: any) => {
    const newSetup = { ...currentSetup, [field]: value };

    if (onDispatchSetupChange) {
      onDispatchSetupChange(newSetup);
    }
  };

  // Separate recipientUserIds into UUIDs and dynamic templates
  const separateRecipientIds = (recipientIds: string[] | undefined) => {
    const uuids: string[] = [];
    const templates: string[] = [];

    if (!recipientIds) return { uuids, templates };

    recipientIds.forEach(id => {
      if (id.includes('{{') && id.includes('}}')) {
        templates.push(id);
      } else {
        uuids.push(id);
      }
    });

    return { uuids, templates };
  };

  // Combine UUIDs and templates back into recipientUserIds
  const combineRecipientIds = (uuids: string[], templates: string[]) => {
    return [...uuids, ...templates];
  };

  const handleRecipientIdsChange = (uuids: string[], templates: string[]) => {
    const combinedIds = combineRecipientIds(uuids, templates);
    handleSetupChange('recipientUserIds', combinedIds);
  };

  const { uuids, templates } = separateRecipientIds(currentSetup.recipientUserIds);

  const getSendingBehaviorIcon = (value: string) => {
    switch (value) {
      case 'SEND_AUTOMATICALLY':
        return <SendIcon sx={{ color: '#4caf50', mr: 1 }} />;
      case 'QUEUE_AS_DRAFT':
        return <ScheduleIcon sx={{ color: '#ff9800', mr: 1 }} />;
      case 'DO_NOT_SEND':
        return <StopIcon sx={{ color: '#f44336', mr: 1 }} />;
      default:
        return <SendIcon sx={{ color: '#4caf50', mr: 1 }} />;
    }
  };

  return (
    <Box>
      <Grid container spacing={2}>
        <Grid item xs={12} md={8}>
          <Typography variant="subtitle2" color="text.secondary">
            ID do Template de Mensagem
          </Typography>
          <TextField
            value={currentSetup.notificationMessageTemplateId}
            onChange={e => handleSetupChange('notificationMessageTemplateId', e.target.value)}
            fullWidth
            size="small"
            disabled
            sx={{ minWidth: 400 }}
          />
        </Grid>

        <Grid item xs={12} md={4}>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Comportamento de Envio
          </Typography>
          <FormControl fullWidth size="small" sx={{ minWidth: 100 }}>
            <Select
              value={currentSetup.additionalPayloadDefinitionConfig?.sendingBehavior || ''}
              onChange={e => {
                const newConfig = {
                  ...currentSetup.additionalPayloadDefinitionConfig,
                  sendingBehavior: e.target.value || undefined
                };
                handleSetupChange('additionalPayloadDefinitionConfig', newConfig);
              }}
              input={<OutlinedInput />}
              renderValue={selected => {
                const option = SENDING_BEHAVIOR_OPTIONS.find(opt => opt.value === selected);
                return (
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    {getSendingBehaviorIcon(selected)}
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {option?.label}
                    </Typography>
                  </Box>
                );
              }}>
              {SENDING_BEHAVIOR_OPTIONS.map(option => (
                <MenuItem key={option.value} value={option.value}>
                  <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                    {getSendingBehaviorIcon(option.value)}
                    <Typography variant="body2">{option.label}</Typography>
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12} md={8}>
          <Autocomplete
            size="small"
            multiple
            fullWidth
            value={uuids}
            onChange={(_, newValues) => {
              if (!newValues) return;
              const stringValues = newValues.map(v => String(v));
              handleRecipientIdsChange(stringValues, templates);
            }}
            options={orgUsers.map(user => user.id)}
            getOptionLabel={option => {
              const user = orgUsers.find(u => u.id === option);
              return user ? `${user.name} (${user.email})` : String(option);
            }}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => {
                const user = orgUsers.find(u => u.id === option);
                return (
                  <Chip
                    variant="outlined"
                    label={user ? `${user.name} (${user.email})` : String(option)}
                    size="small"
                    {...getTagProps({ index })}
                  />
                );
              })
            }
            renderInput={params => (
              <TextField
                {...params}
                fullWidth
                label="Selecionar usuários"
                placeholder="Digite para buscar usuários..."
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {isLoadingUsers && <CircularProgress size={20} />}
                      {params.InputProps.endAdornment}
                    </>
                  )
                }}
              />
            )}
            disabled={isLoadingUsers}
            loading={isLoadingUsers}
            sx={{
              minWidth: 400,
              '& .MuiFormControl-root': {
                width: '100%'
              }
            }}
            noOptionsText={orgUsers.length === 0 ? 'Nenhum usuário encontrado' : 'Nenhuma opção'}
          />
        </Grid>

        <Grid item xs={12}>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Usuários do contexto
          </Typography>

          <Box sx={{ overflow: 'auto', border: 1, borderColor: 'divider', borderRadius: 1, p: 1 }}>
            {generateDynamicTemplates(engineSourceFields).map(template => (
              <Box key={template.value} sx={{ display: 'flex', alignItems: 'center', py: 0.5 }}>
                <Checkbox
                  checked={templates.indexOf(template.value) > -1}
                  onChange={e => {
                    const newTemplates = e.target.checked
                      ? [...templates, template.value]
                      : templates.filter(t => t !== template.value);
                    handleRecipientIdsChange(uuids, newTemplates);
                  }}
                  size="small"
                />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="body2" noWrap>
                    {template.label}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" noWrap>
                    {template.value}
                  </Typography>
                </Box>
              </Box>
            ))}
            {generateDynamicTemplates(engineSourceFields).length === 0 && (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ textAlign: 'center', py: 2 }}>
                Nenhum template dinâmico disponível
              </Typography>
            )}
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
}
